import { FormError } from '@/libs/core/src/lib/types/ErrorResponse.type';
import dayjs from 'dayjs';

// Common props for both pickers
export interface BaseDatePickerProps {
  label?: React.ReactNode | string;
  className?: string;
  /**
   * Optional additional className for the underlying input element
   * (used to customize appearance in specific contexts).
   */
  inputClassName?: string;
  placeholder?: string;
  outputFormat?: string;
  outputType?: 'number' | 'string' | 'dayjs' | 'date';
  showIcon?: boolean;
  disabled?: boolean;
  errors?: FormError;
}

// Type utilities for date values
export type DateValue = number | string | dayjs.Dayjs | Date | undefined;
export type DateRangeValue = {
  from: DateValue;
  to: DateValue;
};

// Shortcut types for date ranges
export type DateRangeShortcut =
  | 'yesterday'
  | 'today'
  | 'tomorrow'
  | 'thisWeek'
  | 'lastWeek'
  | 'nextWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisYear'
  | 'lastYear';

// Shortcut types for single dates
export type DateShortcut =
  | 'yesterday'
  | 'today'
  | 'tomorrow'
  | 'startOfThisWeek'
  | 'startOfLastWeek'
  | 'startOfNextWeek'
  | 'startOfThisMonth'
  | 'startOfLastMonth'
  | 'startOfThisYear'
  | 'startOfLastYear';

export interface CustomShortcut {
  label: string;
  from: dayjs.Dayjs;
  to: dayjs.Dayjs;
}

export interface CustomDateShortcut {
  label: string;
  date: dayjs.Dayjs;
}

export interface CustomClickShortcut {
  label: string;
  onClick: () => void;
}

export interface RelativeShortcut {
  label: string;
  value: number;
  unit: 'days' | 'weeks' | 'months' | 'years';
  direction: 'past' | 'future';
}

export interface RelativeDateShortcut {
  label: string;
  value: number;
  unit: 'days' | 'weeks' | 'months' | 'years';
  direction: 'past' | 'future';
}

export type ShortcutOption = DateRangeShortcut | CustomShortcut | RelativeShortcut;
export type DateShortcutOption =
  | DateShortcut
  | CustomDateShortcut
  | RelativeDateShortcut
  | CustomClickShortcut;

// Output type utilities
export type DateOutput<T extends 'number' | 'string' | 'dayjs' | 'date' | undefined> =
  | (T extends 'string' ? string : T extends 'dayjs' ? dayjs.Dayjs : Date)
  | (T extends 'number' ? number : T extends 'dayjs' ? dayjs.Dayjs : Date)
  | (T extends 'date' ? Date : T extends 'dayjs' ? dayjs.Dayjs : Date)
  | undefined;

export type DateRangeOutput<T extends 'number' | 'string' | 'dayjs' | 'date' | undefined> = {
  from: DateOutput<T>;
  to: DateOutput<T>;
};

// Single date picker props
export interface DatePickerProps<T extends 'number' | 'string' | 'dayjs' | 'date' = 'dayjs'>
  extends BaseDatePickerProps {
  value?: DateValue;
  onChange?: (date: DateOutput<T> | undefined) => void;
  outputType?: T;
  displayFormat?: string;
  closeOnSelection?: boolean;
  clearable?: boolean;
  shortcuts?: DateShortcutOption[];
  minDate?: DateValue; // Minimum selectable date
  maxDate?: DateValue; // Maximum selectable date
}

// Date range picker props
export interface DateRangePickerProps<T extends 'number' | 'string' | 'dayjs' | 'date' = 'dayjs'>
  extends BaseDatePickerProps {
  value?: DateRangeValue;
  onChange?: (date: DateRangeOutput<T> | undefined) => void;
  outputType?: T;
  displayFormat?: string;
  showNavigation?: boolean;
  shortcuts?: ShortcutOption[];
  showShortcuts?: boolean;
  clearable?: boolean;
  onClear?: () => void;
  requireConfirm?: boolean;
  maxDateRange?: number; // Maximum number of days allowed in the range
  allowSameDay?: boolean; // allow single-day date range
}

// Helper to convert any date value to dayjs
export function toDayjs(value: DateValue | undefined): dayjs.Dayjs | undefined {
  if (!value) return undefined;

  return dayjs(value);
}

// Helper to convert dayjs to desired output type
export function toOutputType<T extends 'number' | 'string' | 'dayjs' | 'date'>(
  value: dayjs.Dayjs | undefined,
  type: T,
  format?: string
): DateOutput<T> | undefined {
  if (!value) return undefined;

  switch (type) {
    case 'number':
      return value.valueOf() as DateOutput<T>;
    case 'string':
      return value.format(format || 'YYYY-MM-DD') as DateOutput<T>;
    case 'date':
      return value.toDate() as DateOutput<T>;
    case 'dayjs':
    default:
      return value as DateOutput<T>;
  }
}

// Helper to get date range from shortcut
export function getShortcutRange(shortcut: ShortcutOption): { from: dayjs.Dayjs; to: dayjs.Dayjs } {
  const now = dayjs();

  if (typeof shortcut === 'string') {
    switch (shortcut) {
      case 'yesterday':
        return { from: now.subtract(1, 'day'), to: now.subtract(1, 'day') };
      case 'today':
        return { from: now, to: now };
      case 'tomorrow':
        return { from: now.add(1, 'day'), to: now.add(1, 'day') };
      case 'thisWeek':
        return { from: now.startOf('week'), to: now.endOf('week') };
      case 'lastWeek':
        return {
          from: now.subtract(1, 'week').startOf('week'),
          to: now.subtract(1, 'week').endOf('week'),
        };
      case 'nextWeek':
        return {
          from: now.add(1, 'week').startOf('week'),
          to: now.add(1, 'week').endOf('week'),
        };
      case 'thisMonth':
        return { from: now.startOf('month'), to: now.endOf('month') };
      case 'lastMonth':
        return {
          from: now.subtract(1, 'month').startOf('month'),
          to: now.subtract(1, 'month').endOf('month'),
        };
      case 'thisYear':
        return { from: now.startOf('year'), to: now.endOf('year') };
      case 'lastYear':
        return {
          from: now.subtract(1, 'year').startOf('year'),
          to: now.subtract(1, 'year').endOf('year'),
        };
      default:
        return { from: now, to: now };
    }
  } else if ('from' in shortcut && 'to' in shortcut) {
    // CustomShortcut
    return { from: shortcut.from, to: shortcut.to };
  } else if ('value' in shortcut && 'unit' in shortcut && 'direction' in shortcut) {
    // RelativeShortcut
    const { value, unit, direction } = shortcut;
    const baseDate = direction === 'past' ? now.subtract(value, unit) : now.add(value, unit);

    return { from: baseDate, to: now };
  }

  return { from: now, to: now };
}

// Helper to get single date from shortcut
export function getShortcutDate(shortcut: DateShortcutOption): dayjs.Dayjs {
  const now = dayjs();

  if (typeof shortcut === 'string') {
    switch (shortcut) {
      case 'yesterday':
        return now.subtract(1, 'day');
      case 'today':
        return now;
      case 'tomorrow':
        return now.add(1, 'day');
      case 'startOfThisWeek':
        return now.startOf('week');
      case 'startOfLastWeek':
        return now.subtract(1, 'week').startOf('week');
      case 'startOfNextWeek':
        return now.add(1, 'week').startOf('week');
      case 'startOfThisMonth':
        return now.startOf('month');
      case 'startOfLastMonth':
        return now.subtract(1, 'month').startOf('month');
      case 'startOfThisYear':
        return now.startOf('year');
      case 'startOfLastYear':
        return now.subtract(1, 'year').startOf('year');
      default:
        return now;
    }
  } else if ('date' in shortcut) {
    // CustomDateShortcut
    return shortcut.date;
  } else if ('onClick' in shortcut) {
    // CustomClickShortcut - return current date as we won't use it
    return now;
  } else if ('value' in shortcut && 'unit' in shortcut && 'direction' in shortcut) {
    // RelativeDateShortcut
    const { value, unit, direction } = shortcut;

    return direction === 'past' ? now.subtract(value, unit) : now.add(value, unit);
  }

  return now;
}

// Helper function to parse typed date input (e.g., "01/15/2025")
export const parseDateInput = (
  input: string,
  displayFormat: string = 'MM/DD/YYYY'
): dayjs.Dayjs | null => {
  if (!input?.trim()) return null;

  // Try parsing with the display format first (strict)
  const strictDate = dayjs(input.trim(), displayFormat, true);

  if (strictDate.isValid()) {
    return strictDate;
  }

  // Try flexible parsing
  const flexibleDate = dayjs(input.trim());

  if (flexibleDate.isValid()) {
    return flexibleDate;
  }

  return null;
};

// Helper function to format date for display
export const formatDateForDisplay = (
  date?: dayjs.Dayjs,
  displayFormat: string = 'MM/DD/YYYY'
): string => {
  if (!date) return '';

  return date.format(displayFormat);
};

// Helper to validate date range against maximum allowed days
export function validateDateRange(
  from: dayjs.Dayjs | Date | undefined,
  to: dayjs.Dayjs | Date | undefined,
  maxDays?: number
): { isValid: boolean; actualDays: number; maxDays: number } {
  if (!from || !to || !maxDays) {
    return { isValid: true, actualDays: 0, maxDays: maxDays || 0 };
  }

  const fromDay = dayjs(from);
  const toDay = dayjs(to);

  // Calculate the difference in days (inclusive)
  const actualDays = toDay.diff(fromDay, 'day') + 1;

  return {
    isValid: actualDays <= maxDays,
    actualDays,
    maxDays,
  };
}
